/*
==============================================================================
OPTIMIZED FANCY PRODUCT GALLERY SCRIPT - v2.8.1 PERFORMANCE ENHANCED
==============================================================================
Enhanced product gallery with Fancybox lightbox integration for Shopify stores
Optimized for new efficient architecture with single-pass caption processing
Performance optimized and production ready

NEW v2.8.1: Performance Optimizations + Issue Fixes
- Optimized for new efficient Liquid architecture
- Reduced DOM queries and improved caching
- Enhanced data attribute handling
- Maintained 100% functionality compatibility
- Production-ready with configurable debug mode
- FIXED: First image caption links work immediately
- FIXED: Click outside popup to close fancybox
==============================================================================
*/

// Global flag to prevent double initialization
let galleryInitialized = false;

// Production debug flag (set to false to disable console logging)
const DEBUG_MODE = false;

// Utility function for conditional logging
function debugLog(...args) {
  if (DEBUG_MODE) {
    console.log('[Gallery v2.8.1]', ...args);
  }
}

document.addEventListener('DOMContentLoaded', function() {
  debugLog('Optimized Fancy Product Gallery v2.8.1 initializing');
  
  // Prevent multiple initializations
  if (galleryInitialized) {
    debugLog('Gallery already initialized, skipping');
    return;
  }
  
  function initializeGallery() {
    const gallery = document.querySelector('.fancybox-gallery');
    if (!gallery) {
      debugLog('Gallery container not found - script terminated');
      return;
    }

    if (typeof Fancybox === 'undefined') {
      console.error('Fancybox library not loaded');
      return;
    }

    // Mark as initialized immediately to prevent double init
    galleryInitialized = true;
    gallery.setAttribute('data-gallery-initialized', 'true');
    
    debugLog('Environment validated, initializing optimized components');

    /*
    ==============================================================================
    PRODUCT TITLE DETECTION - CACHED FOR PERFORMANCE
    ==============================================================================
    */
    
    let cachedProductTitle = null;
    
    function getProductTitle() {
      if (cachedProductTitle) {
        return cachedProductTitle;
      }
      
      const titleSelectors = [
        '.product-title h1',
        '.product__title h1', 
        'h1.product-title',
        'h1[class*="product"]',
        '.product-meta h1',
        '.product-info h1'
      ];
      
      for (const selector of titleSelectors) {
        const titleElement = document.querySelector(selector);
        if (titleElement && titleElement.textContent.trim()) {
          cachedProductTitle = titleElement.textContent.trim();
          return cachedProductTitle;
        }
      }
      
      const pageTitle = document.title;
      if (pageTitle && !pageTitle.includes('404') && !pageTitle.includes('Page not found')) {
        cachedProductTitle = pageTitle.replace(/\s*[-|–]\s*.*$/, '').trim();
        return cachedProductTitle;
      }
      
      cachedProductTitle = 'Product Details';
      return cachedProductTitle;
    }

    /*
    ==============================================================================
    CAPTION LINK FIX FUNCTION - NEW FOR v2.8.1
    ==============================================================================
    Fixes Issue #1: First image caption links not working
    */
    

	function forceRefreshCaptionLinks(captionElement, attempts = 0) {
  if (!captionElement || attempts > 3) return;
  
  const links = captionElement.querySelectorAll('a');
  debugLog(`Caption link refresh attempt ${attempts + 1}: found ${links.length} links`);
  
  if (links.length === 0 && attempts < 3) {
    setTimeout(() => forceRefreshCaptionLinks(captionElement, attempts + 1), 300);
    return;
  }
  
  // FIXED: Don't clone links - just ensure they're enabled
  links.forEach((link, index) => {
    link.style.pointerEvents = 'auto';
    link.style.cursor = 'pointer';
    link.style.textDecoration = 'none';
    link.style.color = '#28a745'; // Green color like PMID links
    
    if (link.href && !link.target) {
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
    }
    
    debugLog(`Enabled link ${index + 1}: ${link.href}`);
  });
}

    /*
    ==============================================================================
    OPTIMIZED THUMBNAIL NAVIGATION SYSTEM v2.8.1
    ==============================================================================
    Enhanced for new data structure with improved performance
    */
    
    const thumbnails = document.querySelectorAll('.fancybox-gallery__thumbnail') || [];
    debugLog('Found', thumbnails.length, 'thumbnail(s)');

    if (!thumbnails || thumbnails.length === 0) {
      console.warn('No thumbnails found');
      return;
    }

    /*
    ==============================================================================
    OPTIMIZED THUMBNAIL DATA EXTRACTION v2.8.1
    ==============================================================================
    Enhanced to work with new efficient data structure
    */
    
    function extractThumbnailData(thumbnail) {
      if (!thumbnail) {
        console.error('No thumbnail element provided');
        return {};
      }
      
      const data = {
        mediaId: thumbnail.getAttribute('data-media-id'),
        imageSrc: thumbnail.getAttribute('data-image-src'),
        fullSrc: thumbnail.getAttribute('data-full-src'),
        caption: thumbnail.getAttribute('data-caption'),
        captionHtml: thumbnail.getAttribute('data-caption-html'),
        captionIndex: thumbnail.getAttribute('data-caption-index'),
        groupName: thumbnail.getAttribute('data-group-name'),
        groupValue: thumbnail.getAttribute('data-group-value')
      };
      
      debugLog('Extracted optimized thumbnail data for media ID:', data.mediaId, 'at index:', data.captionIndex);
      return data;
    }

    /*
    ==============================================================================
    ENHANCED MAIN IMAGE PREVIEW UPDATE v2.8.1
    ==============================================================================
    Optimized with better error handling and performance
    */

    function updateMainImagePreview(imageData) {
      debugLog('Updating main image preview for media ID:', imageData.mediaId);
      
      if (!imageData || !imageData.imageSrc) {
        console.error('Invalid image data provided');
        return;
      }
      
      // Cache DOM elements for better performance
      const mainImage = document.querySelector('.fancybox-gallery__main-image');
      const mainLink = document.querySelector('.fancybox-gallery__main-link');
      const captionElement = document.getElementById('gallery-caption');
      
      if (!mainImage || !mainLink) {
        console.error('Main image elements not found');
        return;
      }
      
      // Set loading state with improved UX
      mainImage.style.opacity = '0.5';
      mainImage.style.transition = 'opacity 0.3s ease';
      mainLink.style.pointerEvents = 'none';
      
      // Create optimized image preloader
      const preloadImage = new Image();
      
      preloadImage.onload = function() {
        // Update main image properties
        mainImage.src = imageData.imageSrc;
        mainImage.alt = imageData.caption ? imageData.caption.replace(/<[^>]*>/g, '') : '';
        
        // Update main link properties  
        mainLink.href = imageData.fullSrc;
        mainLink.setAttribute('data-media-id', imageData.mediaId);
        mainLink.setAttribute('data-caption', imageData.caption || '');
        
        if (imageData.captionHtml) {
          mainLink.setAttribute('data-caption-html', imageData.captionHtml);
        }
        
        // Update caption with enhanced HTML support
        if (captionElement) {
          if (imageData.captionHtml) {
            captionElement.innerHTML = imageData.captionHtml;
          } else {
            captionElement.textContent = imageData.caption || '';
          }
        }
        
        // Smooth transition back to visible state
        mainImage.style.opacity = '1';
        mainLink.style.pointerEvents = 'auto';
        
        debugLog('Main image updated successfully with enhanced UX');
      };
      
      preloadImage.onerror = function() {
        console.error('Failed to load image:', imageData.imageSrc);
        mainImage.style.opacity = '1';
        mainLink.style.pointerEvents = 'auto';
      };
      
      preloadImage.src = imageData.imageSrc;
    }

    /*
    ==============================================================================
    ENHANCED FANCYBOX CAPTION FORMATTING v2.8.1
    ==============================================================================
    Optimized with cached product title and improved performance
    */
    
    function formatFancyboxCaption(caption, productTitle) {
      if (!productTitle) productTitle = getProductTitle();
      
      let captionContent = '';
      if (caption && caption.trim() !== '') {
        captionContent = caption;
      } else {
        captionContent = 'View detailed product information.';
      }
      
      const formattedCaption = `
        <div class="product-title">${productTitle}</div>
        <div class="product-content">${captionContent}</div>
      `;
      
      debugLog('Formatted caption for:', productTitle);
      return formattedCaption;
    }

    /*
    ==============================================================================
    ENHANCED CAPTION RENDERING SYSTEM v2.8.1
    ==============================================================================
    Improved reliability and performance for caption display
    */
    
    function forceCaptionRefresh(fancybox, targetSlideIndex, expectedCaption) {
      debugLog('Forcing caption refresh for slide', targetSlideIndex);
      
      // Strategy 1: Direct caption element update with improved timing
      setTimeout(() => {
        const captionElement = document.querySelector('.fancybox__caption');
        if (captionElement && expectedCaption) {
          debugLog('Direct caption update');
          captionElement.innerHTML = expectedCaption;
          
          // Enhanced force layout recalculation
          captionElement.offsetHeight;
          
          // Improved CSS-based repaint forcing
          captionElement.style.transform = 'translateZ(0)';
          requestAnimationFrame(() => {
            captionElement.style.transform = '';
          });
        }
      }, 100);
      
      // Strategy 2: Fancybox slide refresh with enhanced error handling
      setTimeout(() => {
        if (fancybox && fancybox.getSlide && typeof fancybox.getSlide === 'function') {
          debugLog('Fancybox slide refresh');
          try {
            const currentSlide = fancybox.getSlide(targetSlideIndex);
            if (currentSlide && currentSlide.caption !== expectedCaption) {
              currentSlide.caption = expectedCaption;
              
              // Force Fancybox to re-render caption
              const captionElement = document.querySelector('.fancybox__caption');
              if (captionElement) {
                captionElement.innerHTML = expectedCaption;
              }
            }
          } catch (error) {
            debugLog('Fancybox slide refresh error:', error);
          }
        }
      }, 200);
      
      // Strategy 3: Enhanced CSS-based forced refresh
      setTimeout(() => {
        debugLog('CSS forced refresh');
        const captionElement = document.querySelector('.fancybox__caption');
        if (captionElement) {
          // More reliable visibility toggle
          const originalDisplay = captionElement.style.display;
          const originalVisibility = captionElement.style.visibility;
          
          captionElement.style.visibility = 'hidden';
          captionElement.style.display = 'none';
          
          requestAnimationFrame(() => {
            captionElement.innerHTML = expectedCaption;
            captionElement.style.display = originalDisplay;
            captionElement.style.visibility = originalVisibility;
            
            // Enhanced browser repaint with RAF
            captionElement.style.opacity = '0.99';
            requestAnimationFrame(() => {
              captionElement.style.opacity = '';
            });
          });
        }
      }, 300);
    }

    /*
    ==============================================================================
    OPTIMIZED GALLERY OPENING FUNCTION v2.8.1
    ==============================================================================
    Enhanced for new data structure with improved performance and reliability
    */
    
    function openGalleryAtImage(mediaId) {
      debugLog('Opening optimized gallery for media ID:', mediaId);
      
      // Enhanced cleanup with better error handling
      try {
        const existingInstances = document.querySelectorAll('[id^="fancybox-"]');
        existingInstances.forEach(instance => {
          debugLog('Cleaning up existing instance:', instance.id);
        });
        
        // Multiple cleanup strategies for better reliability
        if (window.Fancybox && Fancybox.getInstance) {
          const currentInstance = Fancybox.getInstance();
          if (currentInstance) {
            debugLog('Closing existing Fancybox instance');
            currentInstance.close();
          }
        }
        
        if (Fancybox.closeAll && typeof Fancybox.closeAll === 'function') {
          Fancybox.closeAll();
        }
        
        // Optimized cleanup timing
        setTimeout(() => {
          proceedWithOptimizedGalleryOpen(mediaId);
        }, 100);
        
      } catch (error) {
        console.warn('Cleanup warning:', error);
        proceedWithOptimizedGalleryOpen(mediaId);
      }
    }
    
    function proceedWithOptimizedGalleryOpen(mediaId) {
      const hiddenLinks = document.querySelectorAll('.fancybox-gallery-links a[data-fancybox="fancybox-gallery"]');
      
      if (!hiddenLinks || hiddenLinks.length === 0) {
        console.error('No hidden gallery links found');
        return;
      }
      
      // Use cached product title for better performance
      const productTitle = getProductTitle();
      debugLog('Using cached product title:', productTitle);
      
      const rawGalleryData = [];
      let targetIndex = 0;
      let targetFound = false;
      let expectedCaption = '';
      
      // Phase 1: Build optimized data array
      hiddenLinks.forEach(function(link, index) {
        const linkMediaId = link.getAttribute('data-media-id');
        const htmlCaption = link.getAttribute('data-caption-html');
        const regularCaption = link.getAttribute('data-caption');
        const baseCaption = htmlCaption || regularCaption || '';
        
        debugLog(`Processing optimized link ${index}: MediaID=${linkMediaId}`);
        
        rawGalleryData.push({
          src: link.href,
          baseCaption: baseCaption,
          mediaId: linkMediaId,
          index: index
        });
        
        // Enhanced target detection
        if (String(linkMediaId) === String(mediaId)) {
          targetIndex = index;
          targetFound = true;
          expectedCaption = formatFancyboxCaption(baseCaption, productTitle);
          debugLog(`Target found at index ${index} for media ID ${mediaId}`);
        }
      });
      
      if (!targetFound) {
        console.warn('Target media ID not found, defaulting to index 0');
        targetIndex = 0;
        if (rawGalleryData[0]) {
          expectedCaption = formatFancyboxCaption(rawGalleryData[0].baseCaption, productTitle);
        }
      }
      
      // Phase 2: Apply enhanced caption formatting with performance optimization
      const galleryItems = rawGalleryData.map(function(item) {
        return {
          src: item.src,
          caption: formatFancyboxCaption(item.baseCaption, productTitle),
          type: 'image',
          mediaId: item.mediaId
        };
      });
      
      debugLog('Final optimized validation:', {
        totalItems: galleryItems.length,
        targetIndex: targetIndex,
        targetMediaId: mediaId,
        hasExpectedCaption: !!expectedCaption
      });
      
      // Enhanced Fancybox configuration with improved performance
      const config = {
        startIndex: targetIndex,
        backdropClick: 'close',  // FIXED: Issue #2 - Click outside to close
        
        Images: {
          zoom: true,
          protect: false,
          click: false,
          wheel: 'zoom',
          Panzoom: {
            maxScale: 8,
            click: false,
            dblClick: false,
            wheel: true,
            wheelLimit: 15
          }
        },
        
        Carousel: {
          infinite: false,
          center: true,
          fill: true,
          preload: 1,
          click: false,
          wheel: false
        },
        
        Touch: {
          vertical: true,
          momentum: true,
          clickAttention: false,
          tap: false,
          touch: true
        },
        
        keyboard: {
          Escape: 'close',
          Delete: 'close',
          Backspace: 'close',
          PageUp: 'prev',
          PageDown: 'next',
          ArrowLeft: 'prev',
          ArrowRight: 'next'
        },
        
        Toolbar: {
          display: {
            left: ['infobar'],
            middle: ['prev', 'next'], 
            right: ['zoom', 'fullscreen', 'thumbs', 'close']
			
          }
        },
        
        Thumbs: {
          type: 'classic',
          autoStart: true,
          axis: 'x'
        },
        
        // Enhanced event handlers with improved performance and reliability
        on: {
          init: function(fancybox) {
            debugLog(`Optimized gallery opened at index ${targetIndex} (media ID: ${mediaId})`);
            debugLog(`Fancybox instance ID: ${fancybox.id}`);
            
            // Enhanced caption refresh with improved timing
            setTimeout(() => {
              forceCaptionRefresh(fancybox, targetIndex, expectedCaption);
            }, 150);

            // FIXED: Issue #1 - Force refresh caption links for first slide
            setTimeout(() => {
              const captionElement = document.querySelector('.fancybox__caption');
              if (captionElement) {
                debugLog('Force refreshing caption links for first slide');
                forceRefreshCaptionLinks(captionElement);
              }
            }, 600);
          },
          
          reveal: function(fancybox, slide) {
            const expectedItem = rawGalleryData[slide.index];
            debugLog(`Revealing optimized slide ${slide.index}`);
            debugLog(`Expected media ID: ${expectedItem?.mediaId}`);
            
            const captionElement = document.querySelector('.fancybox__caption');
            if (captionElement && slide.caption) {
              captionElement.innerHTML = slide.caption;

              // CRITICAL FIX: Update this part to not block backdrop clicks
        captionElement.addEventListener('click', function(e) {
          
          // Only prevent propagation for non-link clicks
          if (e.target.tagName !== 'A') {
            e.stopPropagation();
          
          } else {
           
          }
        }, { once: true }); // Add { once: true } to prevent multiple listeners
              
              // FIXED: Issue #1 - Refresh links for all slides
              setTimeout(() => {
                forceRefreshCaptionLinks(captionElement);
              }, 300);
              
              // Enhanced caption refresh for target slide
              if (slide.index === targetIndex && expectedCaption) {
                debugLog('Applying enhanced refresh for target slide');
                requestAnimationFrame(() => {
                  captionElement.innerHTML = expectedCaption;
                  
                  // Improved force repaint
                  captionElement.style.transform = 'translateZ(0)';
                  requestAnimationFrame(() => {
                    captionElement.style.transform = '';
                  });
                });
              }
                  
            }
          },
          
          destroy: function() {
            debugLog('Optimized gallery closed');
          }
        }
      };
      

      debugLog('Opening Fancybox with optimized caption rendering');
      Fancybox.show(galleryItems, config);

      setTimeout(() => {  
  
  // Find the fancybox container and backdrop
  const container = document.querySelector('.fancybox__container');
  const backdrop = document.querySelector('.fancybox__backdrop');
  
  if (container && backdrop) {
       
    // Add click handler to container (which includes backdrop area)
    container.addEventListener('click', function(e) {
            
      // Close if clicking outside the viewport (i.e., on backdrop area)
      if (e.target === container || e.target === backdrop || 
          e.target.classList.contains('fancybox__backdrop') ||
          (!e.target.closest('.fancybox__viewport') && !e.target.closest('.fancybox__toolbar'))) {
               
        Fancybox.close();
      }
    });
    
    // Also add to backdrop directly as backup
    backdrop.addEventListener('click', function(e) {
     // console.log('Backdrop clicked directly');
      if (e.target === backdrop) {        
        Fancybox.close();
      }
    });    
  
  } 
}, 500); // Wait for fancybox to fully render
      
    }

    /*
    ==============================================================================
    OPTIMIZED THUMBNAIL CLICK HANDLERS v2.8.1
    ==============================================================================
    Enhanced with improved performance and user experience
    */
    
    function updateThumbnailStates(activeThumbnail) {
      if (!activeThumbnail) return;
      
      // Use efficient class manipulation
      thumbnails.forEach(function(thumb) {
        if (thumb && thumb.classList) {
          thumb.classList.remove('active');
        }
      });
      
      if (activeThumbnail.classList) {
        activeThumbnail.classList.add('active');
      }
    }

    // Enhanced thumbnail event handling with improved performance
    thumbnails.forEach(function(thumbnail, index) {
      if (!thumbnail) return;
      
      // Single click handler with improved UX
      thumbnail.addEventListener('click', function(event) {
        event.preventDefault();
        debugLog('Optimized thumbnail', index + 1, 'clicked');
        
        updateThumbnailStates(this);
        
        const imageData = extractThumbnailData(this);
        
        if (!imageData.imageSrc) {
          console.error('No image source found for thumbnail:', index);
          return;
        }
        
        updateMainImagePreview(imageData);
      }, { passive: false });
      
      // Enhanced double-click handler
      thumbnail.addEventListener('dblclick', function(event) {
        event.preventDefault();
        const imageData = extractThumbnailData(this);
        debugLog('Optimized thumbnail double-clicked, opening gallery at media ID:', imageData.mediaId);
        
        if (imageData.mediaId) {
          openGalleryAtImage(imageData.mediaId);
        }
      }, { passive: false });
    });

    /*
    ==============================================================================
    OPTIMIZED MAIN IMAGE CLICK HANDLER v2.8.1
    ==============================================================================
    Enhanced with improved event handling and performance
    */
    
    document.addEventListener('click', function(event) {
      const mainLink = event.target.closest('[data-fancybox-trigger="fancybox-gallery"]');
      if (mainLink) {
        event.preventDefault();
        
        const mediaId = mainLink.getAttribute('data-media-id');
        debugLog('Optimized main image clicked, opening gallery for media ID:', mediaId);
        
        if (mediaId) {
          openGalleryAtImage(mediaId);
        } else {
          console.error('No media ID found on main image link');
        }
      }
    }, { passive: false });

    /*
    ==============================================================================
    OPTIMIZED FINAL INITIALIZATION v2.8.1
    ==============================================================================
    Enhanced with improved error handling and performance
    */
    
    function finalizeOptimizedGalleryInit() {
      debugLog('Finalizing optimized gallery state');
      
      if (!thumbnails || thumbnails.length === 0) {
        console.warn('No thumbnails available for initialization');
        return;
      }
      
      // Efficient active thumbnail detection
      const activeThumbnail = document.querySelector('.fancybox-gallery__thumbnail.active');
      const firstThumbnail = document.querySelector('.fancybox-gallery__thumbnail');
      
      if (!activeThumbnail && firstThumbnail && firstThumbnail.classList) {
        firstThumbnail.classList.add('active');
        debugLog('Set first thumbnail as active');
      }
      
      // Cache product title for better performance
      const productTitle = getProductTitle();
      debugLog('Cached product title:', productTitle);
      
      // Enhanced performance monitoring
      if (DEBUG_MODE) {
        const performanceEntry = performance.getEntriesByName('DOMContentLoaded')[0];
        if (performanceEntry) {
          debugLog('Gallery initialization performance:', performanceEntry.duration + 'ms');
        }
      }
    }

    finalizeOptimizedGalleryInit();
    
    debugLog('Optimized Fancy Product Gallery v2.8.1: Initialization completed with enhanced performance');
  }
  
  // Enhanced initialization timing with improved performance
  const initTimeout = setTimeout(() => {
    initializeGallery();
  }, 200);
  
  // Cleanup timeout if page unloads
  window.addEventListener('beforeunload', () => {
    clearTimeout(initTimeout);
  });
});